import { useState, useEffect, useCallback } from 'react';
import type { Order, OrderStatus } from '@shared/types';
import type { OrdersResponse } from '@shared/api-responses';
import { useStations } from '../hooks/useStations';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../hooks/useApi';

type StatusFilter = 'all' | OrderStatus;

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-ems-amber/15 text-ems-amber border border-ems-amber/20',
  in_progress: 'bg-blue-500/15 text-blue-400 border border-blue-500/20',
  filled: 'bg-ems-green/15 text-ems-green border border-ems-green/20',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  filled: 'Filled',
};

export default function Orders() {
  const { stations } = useStations();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const stationNameMap = new Map(stations.map((s) => [s.id, s.name]));

  // Fetch orders
  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<OrdersResponse>('/orders')
      .then((data) => setOrders(data.orders))
      .catch((err) => {
        setOrders([]);
        setError(err instanceof Error ? err.message : 'Failed to load orders');
      })
      .finally(() => setLoading(false));
  }, []);

  const togglePickList = useCallback(
    (orderId: number) => {
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
    },
    [expandedOrder],
  );

  // Mark an order fulfilled in a single step (pending or in_progress → filled).
  const handleFulfill = useCallback(
    async (orderId: number) => {
      const filledBy = user?.name ?? 'Unknown';

      // Optimistic update
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId
            ? { ...o, status: 'filled' as OrderStatus, filled_by: filledBy, filled_at: new Date().toISOString() }
            : o,
        ),
      );
      setActionLoading(orderId);

      try {
        await apiFetch('/orders', {
          method: 'PUT',
          body: { orderId, status: 'filled', filledBy },
        });
      } catch {
        // Revert on failure — refetch
        setError('Failed to update order. Please try again.');
        try {
          const data = await apiFetch<OrdersResponse>('/orders');
          setOrders(data.orders);
        } catch {
          // leave optimistic state
        }
      } finally {
        setActionLoading(null);
      }
    },
    [user],
  );

  // Soft-delete an order (removes it from the list; record is retained server-side).
  const handleDelete = useCallback(async (orderId: number) => {
    setActionLoading(orderId);
    // Optimistic removal
    setOrders((prev) => prev.filter((o) => o.id !== orderId));

    try {
      await apiFetch(`/orders/${orderId}`, { method: 'DELETE' });
    } catch {
      // Revert on failure — refetch
      setError('Failed to delete order. Please try again.');
      try {
        const data = await apiFetch<OrdersResponse>('/orders');
        setOrders(data.orders);
      } catch {
        // leave optimistic state
      }
    } finally {
      setActionLoading(null);
    }
  }, []);

  const filtered = statusFilter === 'all' ? orders : orders.filter((o) => o.status === statusFilter);

  const filterButtons: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'filled', label: 'Filled' },
  ];

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-dvh bg-surface text-white pb-20 md:pb-6">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border-subtle">
        <h1 className="text-xl font-bold">Resupply Orders</h1>
        <p className="text-zinc-500 text-sm mt-0.5">Pick lists and order fulfillment</p>
      </div>

      {/* Status filter tabs */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {filterButtons.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                statusFilter === f.key
                  ? 'bg-dcvfd-accent text-white shadow-sm'
                  : 'bg-surface-raised text-zinc-400 hover:text-white border border-border-subtle'
              }`}
            >
              {f.label}
              {f.key !== 'all' && (
                <span className="ml-1.5 text-xs opacity-70">{orders.filter((o) => o.status === f.key).length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 md:max-w-4xl md:mx-auto">
        {error && (
          <div className="mb-4 rounded-xl bg-red-950/50 border border-red-900/50 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-dcvfd-accent border-t-transparent rounded-full" />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-14 w-14 rounded-2xl bg-surface-raised border border-border-subtle flex items-center justify-center mb-4">
              <svg className="h-7 w-7 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <p className="text-zinc-400 font-medium">
              {statusFilter === 'all' ? 'No orders yet' : `No ${STATUS_LABELS[statusFilter]?.toLowerCase()} orders`}
            </p>
            <p className="text-zinc-600 text-sm mt-1">Orders are created automatically when shortages are detected</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((order) => {
              const isExpanded = expandedOrder === order.id;
              const isActionLoading = actionLoading === order.id;

              return (
                <div key={order.id} className="rounded-2xl bg-surface-raised p-4 border border-border-subtle">
                  {/* Order header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-white">
                        {stationNameMap.get(order.station_id) ?? `Station ${order.station_id}`}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {formatDate(order.created_at)} {formatTime(order.created_at)}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status] ?? ''}`}
                    >
                      {STATUS_LABELS[order.status] ?? order.status}
                    </span>
                  </div>

                  {/* Item count */}
                  <p className="text-sm text-zinc-400 mb-3">
                    {order.items_short} item{order.items_short !== 1 ? 's' : ''} short
                  </p>

                  {/* Filled info */}
                  {order.status === 'filled' && (order.filled_by || order.filled_at) && (
                    <div className="mb-3 rounded-xl bg-ems-green/5 border border-ems-green/15 px-3 py-2">
                      <p className="text-xs text-ems-green">
                        {order.filled_by && <>Filled by {order.filled_by}</>}
                        {order.filled_by && order.filled_at && ' \u2014 '}
                        {order.filled_at && (
                          <>
                            {formatDate(order.filled_at)} {formatTime(order.filled_at)}
                          </>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Pick list toggle / print */}
                  {order.pick_list && (
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => togglePickList(order.id)}
                        className="text-xs text-dcvfd-accent hover:text-dcvfd-accent/80 transition-colors font-medium"
                      >
                        {isExpanded ? 'Hide Pick List' : 'View Pick List'}
                      </button>
                      <button
                        type="button"
                        onClick={() => window.open(`/orders/${order.id}/print`, '_blank', 'noopener')}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface px-2.5 py-1 text-xs font-medium text-zinc-300 hover:bg-surface-overlay hover:text-white transition-colors"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z"
                          />
                        </svg>
                        Print
                      </button>
                      {isExpanded && (
                        <pre className="basis-full mt-2 rounded-xl bg-surface border border-border-subtle px-3 py-2 text-xs text-zinc-300 whitespace-pre-wrap font-mono overflow-x-auto max-h-64 overflow-y-auto">
                          {order.pick_list}
                        </pre>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-2">
                    {order.status !== 'filled' && (
                      <button
                        type="button"
                        disabled={isActionLoading}
                        onClick={() => handleFulfill(order.id)}
                        className="flex-1 rounded-xl bg-ems-green hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 text-sm font-medium text-white transition-all active:scale-[0.98]"
                      >
                        {isActionLoading ? 'Updating...' : 'Fulfilled'}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={isActionLoading}
                      onClick={() => handleDelete(order.id)}
                      aria-label="Delete order"
                      className={`inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-900/50 bg-red-950/40 px-3 py-2.5 text-sm font-medium text-red-300 hover:bg-red-900/40 hover:text-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98] ${
                        order.status === 'filled' ? 'w-full' : ''
                      }`}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
